import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || attempts > 10) { setStatus("error"); return; }
      } catch {
        if (attempts > 10) { setStatus("error"); return; }
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-emerald-950 emerald-grain flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-emerald-600 animate-spin" size={44} />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-emerald-950">Confirming payment…</h1>
            <p className="mt-2 text-slate-500">Hang tight while we finalize your reservation.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={52} />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-emerald-950">Booking confirmed!</h1>
            <p className="mt-2 text-slate-500">A confirmation email is on its way. Enjoy the ride.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 w-full bg-emerald-900 hover:bg-emerald-800 rounded-xl h-11" data-testid="go-dashboard">
              View my bookings
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-red-500" size={52} />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-emerald-950">Something went wrong</h1>
            <p className="mt-2 text-slate-500">We couldn't confirm your payment. Please check your dashboard.</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-6 w-full bg-emerald-900 hover:bg-emerald-800 rounded-xl h-11">Go to dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-emerald-950 emerald-grain flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl" data-testid="payment-cancel">
        <XCircle className="mx-auto text-amber-500" size={52} />
        <h1 className="mt-5 font-display text-2xl font-extrabold text-emerald-950">Payment cancelled</h1>
        <p className="mt-2 text-slate-500">No charge was made. Your booking is saved as unpaid — you can complete it anytime.</p>
        <Button onClick={() => navigate("/dashboard")} className="mt-6 w-full bg-emerald-900 hover:bg-emerald-800 rounded-xl h-11">Go to dashboard</Button>
      </div>
    </div>
  );
}
